<template lang="pug">
#modal-vincular-contratos
    el-dialog.modal-vincular-contratos-content(
        :visible.sync='show',
        :close-on-click-modal='false',
        :close-on-press-escape='false',
        :destroy-on-close='true',
        :show-close='false',
        width='80%'
    )
        h1(slot='title') {{ $t('mensagens.sucursales.tituloModal') }}

        .modal-vincular-contratos-content__container
            .container
                .contract-section
                    .form-row
                        .form-group
                            label.form-label {{ $t('mensagens.sucursales.selecioneContrato') }}
                            el-select(
                                v-model='contratoParaAdicionar',
                                :placeholder='$t("mensagens.sucursales.placeholderContrato")',
                                filterable,
                                remote,
                                clearable,
                                :disabled='Boolean(contratoSelecionado)',
                                :remote-method='debounceSearch',
                                :loading='loadingContratos',
                                value-key='id',
                                @visible-change='handleContratoSelectVisible'
                            )
                                el-option(
                                    v-for='contrato in contratos',
                                    :key='contrato.id',
                                    :label='formatarLabelContrato(contrato)',
                                    :value='contrato'
                                )
                        .action-group
                            el-button.add-button(
                                type='primary',
                                icon='el-icon-plus',
                                :disabled='!contratoParaAdicionar || Boolean(contratoSelecionado)',
                                @click='adicionarContrato'
                            )

                    .contract-readonly(v-if='contratoSelecionado')
                        .form-group
                            label.form-label {{ $t('mensagens.sucursales.numeroContrato') }}
                            el-input(
                                :value='contratoSelecionado.numeroContrato',
                                readonly
                            )
                        .form-group
                            label.form-label {{ $t('mensagens.sucursales.valorContrato') }}
                            el-input(
                                :value='formatarVolume(obterVolumeContrato(contratoSelecionado))',
                                readonly
                            )
                        .action-group
                            el-button.remove-contract-button(
                                type='danger',
                                @click='removerContrato'
                            )
                                i.mdi.mdi-delete(style='margin-right: 5px')
                                | {{ $t('mensagens.sucursales.removerContrato') }}

                .divider

                .branch-section
                    .form-row
                        .form-group
                            label.form-label {{ $t('mensagens.sucursales.selecioneSucursal') }}
                            el-select(
                                v-model='sucursalSelecionada',
                                :placeholder='$t("mensagens.sucursales.placeholderSucursal")',
                                filterable,
                                clearable
                            )
                                el-option(
                                    v-for='sucursal in sucursaisDisponiveis',
                                    :key='sucursal.id',
                                    :label='sucursal.nome',
                                    :value='sucursal.id'
                                )
                        .form-group.volume-group
                            label.form-label {{ $t('mensagens.sucursales.volumePercentual') }}
                            el-input-number(
                                v-model='percentualSucursal',
                                :min='0',
                                :max='100',
                                :precision='2',
                                :controls='false',
                                placeholder='0'
                            )
                        .action-group
                            el-button.add-button(
                                type='primary',
                                icon='el-icon-plus',
                                :disabled='!podeAdicionarSucursal',
                                @click='adicionarSucursal'
                            )

                    .contracts-section
                        .contracts-table
                            el-table(
                                :data='sucursaisVinculadas',
                                style='width: 100%',
                                :empty-text='$t("mensagens.sucursales.nenhumaSucursalAdicionada")'
                            )
                                el-table-column(
                                    prop='nome',
                                    :label='$t("mensagens.sucursales.nomeSucursal")'
                                )
                                el-table-column(
                                    prop='percentual',
                                    label='%'
                                )
                                    template(slot-scope='scope')
                                        span {{ formatarPercentual(scope.row.percentual) }}
                                el-table-column(
                                    prop='volumeRepresentado',
                                    :label='$t("mensagens.sucursales.volumeRepresentado")'
                                )
                                    template(slot-scope='scope')
                                        span {{ formatarVolume(scope.row.volumeRepresentado) }}
                                el-table-column(
                                    :label='$t("mensagens.comuns.acoes")',
                                    width='120',
                                    align='center'
                                )
                                    template(slot-scope='scope')
                                        el-button(
                                            type='danger',
                                            size='mini',
                                            @click='removerSucursal(scope.$index)'
                                        ) {{ $t('mensagens.comuns.excluir') }}

                    .status-section
                        .status-info
                            span.status-label {{ $t('mensagens.sucursales.statusContrato') }}
                            span.status-value(:class='{ complete: percentualTotal === 100 }') {{ formatarPercentual(percentualTotal) }}
                        el-button(
                            type='primary',
                            @click='enviarContratos',
                            :disabled='!podeEnviarContrato',
                            :loading='loadingEnvio'
                        )
                            i.mdi.mdi-send(style='margin-right: 5px')
                            | {{ $t('mensagens.sucursales.enviarContrato') }}

        .footer
            .buttons
                el-button(@click='cancelar')
                    i.mdi.mdi-close(style='margin-right: 5px')
                    | {{ $t('mensagens.comuns.cancelar') }}
</template>

<script>
import ContratoOriginacaoServiceSubEmpresa from '@/services/ContratoOriginacaoServiceSubEmpresa';

export default {
    name: 'ModalVincularContratos',

    props: {
        show: {
            type: Boolean,
            required: true
        },
        sucursais: {
            type: Array,
            required: true,
            default: () => []
        }
    },

    data() {
        return {
            contratoParaAdicionar: null,
            contratoSelecionado: null,
            sucursalSelecionada: null,
            percentualSucursal: null,
            sucursaisVinculadas: [],
            contratos: [],
            filtroContrato: '',
            loadingContratos: false,
            loadingEnvio: false,
            debounceTimeout: null
        };
    },

    computed: {
        percentualTotal() {
            const total = this.sucursaisVinculadas.reduce(
                (soma, sucursal) => soma + Number(sucursal.percentual || 0),
                0
            );

            return Number(total.toFixed(2));
        },

        sucursaisDisponiveis() {
            const idsVinculados = this.sucursaisVinculadas.map(
                sucursal => sucursal.id
            );

            return this.sucursais.filter(
                sucursal => !idsVinculados.includes(sucursal.id)
            );
        },

        podeAdicionarSucursal() {
            return (
                this.contratoSelecionado &&
                this.sucursalSelecionada &&
                Number(this.percentualSucursal) > 0 &&
                this.percentualTotal + Number(this.percentualSucursal) <= 100
            );
        },

        podeEnviarContrato() {
            return (
                this.contratoSelecionado &&
                this.sucursaisVinculadas.length > 0 &&
                this.percentualTotal === 100
            );
        }
    },

    methods: {
        async carregarContratos(search = '') {
            this.loadingContratos = true;
            try {
                const { data } =
                    await ContratoOriginacaoServiceSubEmpresa.contratosNaoEnviadosSucursais(
                        {
                            page: 0,
                            search: search.trim()
                        }
                    );

                this.contratos = data.itens || [];
            } catch (error) {
                this.$notify.error(this.$t('mensagens.sucursales.erroBuscar'));
            } finally {
                this.loadingContratos = false;
            }
        },

        debounceSearch(query = '') {
            if (this.debounceTimeout) {
                clearTimeout(this.debounceTimeout);
            }

            this.debounceTimeout = setTimeout(() => {
                this.filtroContrato = query;
                this.carregarContratos(query);
            }, 500);
        },

        handleContratoSelectVisible(visible) {
            if (visible && this.contratos.length === 0) {
                this.carregarContratos(this.filtroContrato);
            }
        },

        adicionarContrato() {
            if (!this.contratoParaAdicionar || this.contratoSelecionado) {
                return;
            }

            this.contratoSelecionado = this.contratoParaAdicionar;
            this.contratoParaAdicionar = null;
            this.sucursaisVinculadas = [];
            this.sucursalSelecionada = null;
            this.percentualSucursal = null;
        },

        removerContrato() {
            this.contratoSelecionado = null;
            this.contratoParaAdicionar = null;
            this.sucursaisVinculadas = [];
            this.sucursalSelecionada = null;
            this.percentualSucursal = null;
        },

        adicionarSucursal() {
            if (!this.podeAdicionarSucursal) {
                this.$notify.warning(
                    this.$t('mensagens.sucursales.erroPercentualSucursal')
                );
                return;
            }

            const sucursal = this.sucursais.find(
                item => item.id === this.sucursalSelecionada
            );

            if (!sucursal) return;

            const percentual = Number(this.percentualSucursal);

            this.sucursaisVinculadas.push({
                id: sucursal.id,
                nome: sucursal.nome,
                percentual,
                volumeRepresentado: this.calcularVolumeRepresentado(
                    percentual
                )
            });

            this.sucursalSelecionada = null;
            this.percentualSucursal = null;
        },

        removerSucursal(index) {
            this.sucursaisVinculadas.splice(index, 1);
        },

        calcularVolumeRepresentado(percentual) {
            return (
                (this.obterVolumeContrato(this.contratoSelecionado) *
                    Number(percentual)) /
                100
            );
        },

        obterVolumeContrato(contrato) {
            if (!contrato) return 0;

            const volume =
                contrato.volumeContratado ||
                contrato.valorContrato ||
                contrato.valor ||
                0;

            if (typeof volume === 'number') return volume;

            const normalizado = String(volume)
                .replace(/\./g, '')
                .replace(',', '.')
                .replace(/[^\d.-]/g, '');

            return Number(normalizado) || 0;
        },

        formatarLabelContrato(contrato) {
            if (!contrato) return '';

            const partes = [contrato.numeroContrato];

            if (contrato.material) {
                partes.push(contrato.material);
            }

            if (contrato.safraNome) {
                partes.push(contrato.safraNome);
            }

            return partes.filter(Boolean).join(' - ');
        },

        formatarPercentual(valor) {
            return `${Number(valor || 0).toLocaleString('pt-BR', {
                minimumFractionDigits: 0,
                maximumFractionDigits: 2
            })}%`;
        },

        formatarVolume(valor) {
            return Number(valor || 0).toLocaleString('pt-BR', {
                minimumFractionDigits: 0,
                maximumFractionDigits: 2
            });
        },

        cancelar() {
            this.limparFormulario();
            this.$emit('close');
        },

        async enviarContratos() {
            if (!this.podeEnviarContrato) {
                this.$notify.warning(
                    this.$t('mensagens.sucursales.erroDistribuicaoContrato')
                );
                return;
            }

            this.loadingEnvio = true;
            try {
                const dados = {
                    contrato: this.contratoSelecionado.id,
                    sucursais: this.sucursaisVinculadas.map(sucursal => ({
                        sucursal: sucursal.id,
                        percentual: sucursal.percentual,
                        volumeRepresentado: sucursal.volumeRepresentado
                    }))
                };

                await ContratoOriginacaoServiceSubEmpresa.enviarContratosSucursais(
                    dados
                );

                this.limparFormulario();
                this.$emit('success');

                this.$notify.success(
                    this.$t('mensagens.sucursales.sucessoVinculo')
                );
            } catch (error) {
                this.$notify.error(this.$t('mensagens.sucursales.erroEnvio'));
            } finally {
                this.loadingEnvio = false;
            }
        },

        limparFormulario() {
            this.contratoParaAdicionar = null;
            this.contratoSelecionado = null;
            this.sucursalSelecionada = null;
            this.percentualSucursal = null;
            this.sucursaisVinculadas = [];
            this.contratos = [];
            this.filtroContrato = '';

            if (this.debounceTimeout) {
                clearTimeout(this.debounceTimeout);
                this.debounceTimeout = null;
            }
        }
    },

    watch: {
        show(newVal) {
            if (!newVal) {
                this.limparFormulario();
            } else {
                this.carregarContratos();
            }
        }
    },

    beforeDestroy() {
        if (this.debounceTimeout) {
            clearTimeout(this.debounceTimeout);
        }
    }
};
</script>

<style lang="scss">
#modal-vincular-contratos {
    .el-dialog__header h1 {
        font-size: 1.25rem;
        font-weight: bolder;
    }

    .el-dialog__body {
        padding: 0 25px 0 25px !important;
    }

    .modal-vincular-contratos-content__container {
        max-height: 70vh;
        overflow-y: auto;
        margin-top: 1rem;

        .container {
            display: grid;
            gap: 1.5rem;
            width: 100%;
            max-width: none;
            box-sizing: border-box;
            padding: 0 1.5rem !important;

            .contract-section,
            .branch-section {
                display: grid;
                gap: 1rem;
            }

            .form-row {
                display: grid;
                grid-template-columns: 1fr auto;
                align-items: start;
                gap: 1rem;
            }

            .branch-section .form-row {
                grid-template-columns: minmax(0, 1fr) 180px auto;
            }

            .form-group {
                width: 100%;
                display: flex;
                flex-direction: column;
                gap: 0.5rem;

                .form-label {
                    font-weight: 600;
                    color: var(--primary-font-color);
                    font-size: 0.9rem;
                    line-height: 17px;
                }
            }

            .contract-readonly {
                display: grid;
                grid-template-columns: minmax(0, 1fr) minmax(0, 1fr) auto;
                align-items: start;
                gap: 1rem;
            }

            .divider {
                border-top: 1px solid #e0e0e0;
            }

            .action-group {
                display: flex;
                align-items: stretch;
                height: 36px;
                margin-top: 25px;
            }

            .add-button {
                height: 36px;
                min-width: 48px;
                margin-bottom: 0;
                padding: 0 14px !important;
            }

            .remove-contract-button {
                height: 36px;
                margin-bottom: 0;
                white-space: nowrap;
            }

            .volume-group {
                .el-input-number,
                .el-input {
                    width: 100%;
                }
            }

            .contracts-section {
                display: grid;
                gap: 1rem;

                .contracts-table {
                    border: 1px solid #e0e0e0;
                    border-radius: 8px;
                    overflow: hidden;

                    .el-table {
                        .el-table__header {
                            background-color: #f5f5f5;

                            th {
                                background-color: #f5f5f5 !important;
                                color: var(--primary);
                                font-weight: 600;
                            }
                        }

                        .el-table__row {
                            &:hover {
                                background-color: #f9f9f9;
                            }
                        }
                    }
                }
            }

            .status-section {
                display: flex;
                justify-content: space-between;
                align-items: center;
                gap: 1rem;

                .status-info {
                    display: flex;
                    align-items: center;
                    gap: 2rem;
                    font-weight: 600;
                }

                .status-label {
                    color: var(--primary-font-color);
                    font-size: 1rem;
                }

                .status-value {
                    color: #d9534f;
                    font-size: 1.1rem;

                    &.complete {
                        color: #2ca24d;
                    }
                }
            }
        }
    }

    .footer {
        width: 100%;
        padding: 1rem;

        .buttons {
            border-top: 1px solid #e0e0e0;
            padding-top: 1rem;
            display: flex;
            align-items: center;
            justify-content: flex-end;
            gap: 0.5rem;

            .el-button {
                padding: 8px 20px !important;
            }
        }
    }
}

.modal-vincular-contratos-content {
    .el-dialog__header {
        padding: 20px;
        padding-bottom: 10px;
        background: var(--primary) !important;
        color: var(--white);
        padding-bottom: 12px;
        padding-top: 12px;
    }

    .el-dialog__body {
        padding: 20px;
        word-break: break-word;
    }

    .el-dialog__headerbtn {
        top: 10px;
    }

    .el-dialog__headerbtn:hover .el-icon-close {
        color: #ffffff;
    }
}
</style>
